/**
 * O mapa do produto — um lugar só.
 *
 * ─── POR QUE ISTO EXISTE ──────────────────────────────────────────
 *
 * O casco tinha SEIS botões planos e eles eram a única porta para 24
 * telas. Dezoito só existiam dentro de outra, e o Lucas abriu o produto
 * e perguntou "cadê tudo?". Estava certo.
 *
 * Os grupos aqui não são categorias de OBJETO ("pacientes, agenda,
 * financeiro") — são de INTENÇÃO: onde se trabalha, onde se cuida do
 * dinheiro, onde se ajusta a clínica. Foi a lista por objeto que deixou
 * a régua de WhatsApp, o consentimento e a trilha sem lugar para morar,
 * porque nenhum deles é um objeto do negócio.
 *
 * ─── A REGRA DO SUMIÇO ────────────────────────────────────────────
 *
 * Destino cujo escopo a pessoa não tem **não aparece**. Nunca aparece
 * desativado. `exigirEscopo` responde 404 de propósito (403 num id
 * confirma que o recurso existe em outra clínica e vira oráculo), então
 * um item cinza que, clicado, diz "não encontrado" ensina que o sistema
 * está quebrado. Grupo que fica sem nenhum item some inteiro, título
 * junto.
 */

export type Grupo = 'dia' | 'dinheiro' | 'clinica'

export type Destino = {
  /** Caminho na URL. É o identificador — nada de id paralelo. */
  caminho: string
  rotulo: string
  grupo: Grupo
  /** Escopo exigido. `null` = todo mundo com clínica ativa vê. */
  escopo: string | null
  /**
   * Nome do ícone no `lucide-react`.
   *
   * Era um caractere Unicode, e renderizava irregular entre sistemas:
   * `♁` para Pacientes e `⚕` para Equipe saíam finos demais no macOS e
   * viravam quadrado em Linux sem fonte de símbolo. Ícone de verdade é
   * previsível e escala com o traço.
   */
  icone: string
  /** Some do rail mas continua alcançável por caminho (ex.: ficha). */
  oculto?: boolean
}

export const GRUPOS: Array<{ id: Grupo; titulo: string }> = [
  { id: 'dia', titulo: 'O dia' },
  { id: 'dinheiro', titulo: 'O dinheiro' },
  { id: 'clinica', titulo: 'A clínica' },
]

/**
 * ⚠️ `WhatsApp` fica em "A clínica" e não em "O dia" até existir
 * despachante: hoje nada lê `public.fila_wa` e chama a Meta, então o
 * destino nunca teria movimento.
 */
export const DESTINOS: Destino[] = [
  { caminho: '/',             rotulo: 'Início',       grupo: 'dia',      escopo: null,              icone: 'House' },
  { caminho: '/agenda',       rotulo: 'Agenda',       grupo: 'dia',      escopo: 'agenda:read',     icone: 'CalendarDays' },
  { caminho: '/fila',         rotulo: 'Fila',         grupo: 'dia',      escopo: 'agenda:read',     icone: 'ListChecks' },
  { caminho: '/meu-dia',      rotulo: 'Meu dia',      grupo: 'dia',      escopo: 'agenda:read',     icone: 'UserRoundCheck' },
  { caminho: '/pacientes',    rotulo: 'Pacientes',    grupo: 'dia',      escopo: 'paciente:read',   icone: 'Users' },

  { caminho: '/recebimentos', rotulo: 'Recebimentos', grupo: 'dinheiro', escopo: 'financeiro:read', icone: 'Receipt' },
  { caminho: '/caixa',        rotulo: 'Caixa',        grupo: 'dinheiro', escopo: 'financeiro:read', icone: 'Wallet' },
  /* O repasse exige `financeiro:write` no MENU por decisão de produto —
     e isso NÃO é autorização. `GET /v1/repasse` pede só
     `financeiro:read`, que a RECEPÇÃO tem: pela API ela já lê a
     comissão de todo mundo. Está no relatório de segurança e o conserto
     é na rota, não aqui. Esconder o item não fecha porta nenhuma. */
  { caminho: '/repasse',      rotulo: 'Repasse',      grupo: 'dinheiro', escopo: 'financeiro:write', icone: 'ArrowLeftRight' },
  { caminho: '/relatorios',   rotulo: 'Relatórios',   grupo: 'dinheiro', escopo: 'relatorio:read',  icone: 'ChartNoAxesColumn' },

  /* ── A CLÍNICA COMO CADASTRO ──────────────────────────────────
     Estes quatro estavam TODOS dentro de `/ajustes`, uma tela de 540
     linhas com seis grupos — e a lista de partida do Início mandava
     quatro dos cinco passos para lá, sem dizer para qual grupo. Cada
     tarefa de montagem passa a ter endereço próprio, que é o que
     permite ao roteiro apontar para o lugar exato.

     `/ajustes` deixou de existir: os seis grupos dele já tinham casa
     (conversa → WhatsApp, prontuário → Prontuário, equipe → Equipe,
     conta → Conta e plano) ou ganharam uma aqui. Manter a porta velha
     ao lado das novas daria dois caminhos para a mesma coisa. */
  { caminho: '/identidade',   rotulo: 'Identidade',   grupo: 'clinica',  escopo: null,              icone: 'Building2' },
  { caminho: '/salas',        rotulo: 'Salas',        grupo: 'clinica',  escopo: null,              icone: 'DoorOpen' },
  { caminho: '/equipe',       rotulo: 'Equipe',       grupo: 'clinica',  escopo: null,              icone: 'Stethoscope' },
  { caminho: '/catalogo',     rotulo: 'Catálogo',     grupo: 'clinica',  escopo: null,              icone: 'ClipboardList' },
  { caminho: '/horarios',     rotulo: 'Horários',     grupo: 'clinica',  escopo: null,              icone: 'Clock' },
  { caminho: '/prontuario',   rotulo: 'Prontuário',   grupo: 'clinica',  escopo: 'prontuario:read', icone: 'FileText' },
  { caminho: '/whatsapp',     rotulo: 'WhatsApp',     grupo: 'clinica',  escopo: 'agenda:read',     icone: 'MessageCircle' },
  { caminho: '/trilha',       rotulo: 'Trilha',       grupo: 'clinica',  escopo: 'auditoria:read',  icone: 'ShieldCheck' },
  /* `ContaSeguranca` tem três focos e só DOIS estavam ligados: `tudo` e
     `trilha`. O foco `conta` — sessões abertas, troca de senha e o
     PLANO — não era alcançável por nenhum caminho do app. O efeito
     prático: a mensagem "mude de plano para cadastrar mais" não tinha
     para onde mandar a pessoa. Sem escopo, porque é a conta de quem
     está olhando. */
  { caminho: '/conta',        rotulo: 'Conta e plano', grupo: 'clinica', escopo: null,              icone: 'CircleUser' },
]

/** Caminhos que existem sem sessão. O casco trata cada um antes do gate. */
export const CAMINHOS_PUBLICOS = [
  '/fundador', '/convite', '/recuperar', '/nova-senha', '/confirmar',
] as const

/**
 * Resolve o caminho atual para um destino.
 *
 * Casa o prefixo mais longo, para que `/pacientes/<id>` continue
 * acendendo `Pacientes` no rail. `/` é exato — senão casaria com tudo.
 */
export function destinoDe(caminho: string, pode: (e: string) => boolean): Destino | null {
  const visiveis = DESTINOS.filter((d) => !d.escopo || pode(d.escopo))
  if (caminho === '/' || caminho === '') return visiveis[0] ?? null
  return (
    visiveis
      .filter((d) => d.caminho !== '/' && (caminho === d.caminho || caminho.startsWith(`${d.caminho}/`)))
      .sort((a, b) => b.caminho.length - a.caminho.length)[0] ?? null
  )
}

/** Navega sem recarregar a página. O casco escuta `popstate`. */
export function ir(caminho: string) {
  if (window.location.pathname === caminho) return
  window.history.pushState({}, '', caminho)
  window.dispatchEvent(new PopStateEvent('popstate'))
}
